<template>
  <b-modal no-fade v-model="dialog.show" v-bind:size="id==null ? 'lg' : 'xl'" :ref="`madal-client-${uniqueId}`"
           :id="`madal-client-${uniqueId}`"
           :hide-footer="true" centered @show="closeAllPopovers" @close="onDialogClose"
           no-close-on-backdrop
           dialog-class="cm-dialog"
           body-class="cm-body"
           header-class="cm-modal-header"
  >
    <template #modal-title>
      <span v-if="id == null">{{ $t('tabs.clients.newClient') }}</span>
      <span v-else-if="loading !== true && error == null && client != null">
        <i v-if="client.isTest" class="cm-test-badge">TEST</i>
        {{ client.firstName }} {{ client.lastName }}
      </span>
    </template>

    <div class="cm-content" style="min-height: 620px">
      <!-- ============ COMPACT HERO ============ -->
      <div v-if="loading !== true && error == null && client != null && client.id != null" class="cm-hero">
        <div class="cm-hero-row">
          <div class="cm-avatar" :class="{ online: client.online, offline: !client.online }">
            <span class="cm-avatar-initials">{{ avatarInitials }}</span>
            <span class="cm-presence" v-b-tooltip.hover :title="client.online ? 'Online' : 'Offline'"></span>
          </div>
          <div class="cm-hero-name-block">
            <div class="cm-hero-name">
              <span class="cm-hero-name-text">{{ client.firstName }} {{ client.lastName }}</span>
              <span class="cm-pill cm-pill-test" v-if="client.isTest">TEST</span>
              <span class="cm-pill cm-pill-warn" v-if="client.affiliateSource"
                    v-b-tooltip.hover :title="$t('columns.duplicated')">
                <i class="fa-solid fa-triangle-exclamation"></i> {{ $t('columns.duplicated') }}
              </span>
            </div>
            <div class="cm-hero-sub">
              <span v-if="client.email" class="cm-sub-item"><i class="fa-regular fa-envelope"></i> {{ client.email }}</span>
              <span v-if="client.country" class="cm-sub-item"><i class="fa-regular fa-flag"></i> {{ client.country }}</span>
              <span v-if="client.id" class="cm-sub-item"><i class="fa-regular fa-id-badge"></i> #{{ client.id }}</span>
            </div>
          </div>
          <div class="cm-stats">
            <div class="cm-stat cm-stat-balance">
              <div class="cm-stat-icon"><i class="fa-solid fa-coins"></i></div>
              <div>
                <div class="cm-stat-label">{{ $t('columns.balance') }}</div>
                <div class="cm-stat-value"
                     :class="{ 'cm-stat-positive': client.totalBalance > 0, 'cm-stat-negative': client.totalBalance < 0 }"
                     v-if="clientCurrency">{{ convertToEURO(client.totalBalance, clientCurrency) }}</div>
                <div class="cm-stat-value cm-stat-value-empty" v-else>—</div>
              </div>
            </div>
            <div class="cm-stat cm-stat-credit">
              <div class="cm-stat-icon"><i class="fa-solid fa-credit-card"></i></div>
              <div>
                <div class="cm-stat-label">{{ $t('columns.credit') }}</div>
                <div class="cm-stat-value" v-if="clientCurrency">{{ convertToEURO(client.totalCredit, clientCurrency) }}</div>
                <div class="cm-stat-value cm-stat-value-empty" v-else>—</div>
              </div>
            </div>
          </div>
        </div>

        <!-- ACTION BAR -->
        <div class="cm-action-bar">
          <button class="cm-quick-action" :disabled="!client.phone || !hasPhonePermission"
                  @click="quickCall" :title="$t('operations.call')">
            <i class="fa-solid fa-phone"></i><span>{{ $t('operations.call') }}</span>
          </button>
          <button class="cm-quick-action" @click="quickEmail" :title="$t('operations.sendMail')">
            <i class="fa-solid fa-envelope"></i><span>{{ $t('operations.sendMail') }}</span>
          </button>
          <button class="cm-quick-action cm-quick-primary" @click="quickNote">
            <i class="fa-solid fa-plus"></i><span>{{ $t('logNote') }}</span>
          </button>
          <button class="cm-quick-action" @click="quickSchedule">
            <i class="fa-solid fa-calendar-plus"></i><span>{{ $t('scheduleEvent') }}</span>
          </button>
          <div class="cm-action-spacer"></div>
          <b-dropdown size="sm" variant="outline-primary" class="cm-actions-dropdown" right
                      boundary="viewport" menu-class="cm-actions-menu">
            <template #button-content>
              <i class="fa-solid fa-bolt mr-1"></i>{{ $t('operations.actions') }}
            </template>
            <b-dropdown-item @click="openCopyClientModal">
              <i class="fa-solid fa-copy mr-2"></i>{{ $t('columns.copyClient') }}
            </b-dropdown-item>
            <b-dropdown-item @click="openPromoteClientModal">
              <i class="fa-solid fa-arrows-up-down mr-2"></i>
              {{ (client.isSale == null || client.isSale) ? $t('columns.promoteToClient') : $t('columns.demoteToLead') }}
            </b-dropdown-item>
            <b-dropdown-item @click="openMyAccount" :disabled="loadingToken">
              <i class="fa-solid fa-arrow-right-to-bracket mr-2"></i>{{ $t('columns.login') }}
            </b-dropdown-item>
          </b-dropdown>
          <b-button variant="primary" size="sm" class="cm-save-btn" @click="saveClientDetails">
            <i class="fa-solid fa-floppy-disk mr-1"></i>{{ $t('operations.save') }}
          </b-button>
        </div>
      </div>

      <div v-if="loading !== true && error == null">
        <b-tabs v-if="client != null && client.id != null" v-model="dialog.tab" content-class="cm-tabs-content"
                nav-class="cm-tabs-nav" class="cm-tabs">
          <b-tab :title="$t('tabs.clients.details')" active @click="refreshData">
            <template #title>
              <span class="cm-tab-pill">
                <i class="fas fa-circle-notch fa-spin" v-if="reLoading === true"></i>
                <i class="fa-sharp fa-solid fa-user" v-else></i>
                {{ $t('tabs.clients.details') }}
              </span>
            </template>
            <client-details ref="details" @input="dialog.show=false" v-model="client" @close="dialog.show=false"/>
          </b-tab>

          <b-tab :title="$t('tabs.clients.accounts')" @click="()=>$refs.accounts.refresh()">
            <template #title>
              <span class="cm-tab-pill">
                <i class="fas fa-circle-notch fa-spin" v-if="$refs.accounts && $refs.accounts.loading === true"></i>
                <i class="fa-sharp fa-solid fa-wallet" v-else></i>
                {{ $t('tabs.clients.accounts') }}
              </span>
            </template>
            <client-accounts ref="accounts" :client-id="client.id" v-bind:organization-id="client.orgId" :client="client"/>
          </b-tab>
          <b-tab :title="$t('tabs.clients.monetaryTransaction')" @click="()=>$refs.monetary.refresh()">
            <template #title>
              <span class="cm-tab-pill">
                <i class="fas fa-circle-notch fa-spin" v-if="$refs.monetary && $refs.monetary.loading === true"></i>
                <i class="fa-sharp fa-solid fa-credit-card" v-else></i>
                {{ $t('tabs.clients.monetaryTransaction') }}
              </span>
            </template>
            <client-monetary-table :is-client-table="true" ref="monetary" :client-id="client.id"
                                   :columns="monetaryColumnsNames" :client="client"
                                   @transaction-added="refreshDataAfterMonetary"/>
          </b-tab>
          <b-tab :title="$t('tabs.clients.documents')" @click="()=>$refs.kycTable.refresh()">
            <template #title>
              <span class="cm-tab-pill">
                <i class="fas fa-circle-notch fa-spin" v-if="$refs.kycTable && $refs.kycTable.loading === true"></i>
                <i class="fa-sharp fa-solid fa-file-shield" v-else></i>
                {{ $t('tabs.clients.documents') }}
              </span>
            </template>
            <kyc-table :is-client-table="true" ref="kycTable" v-bind:filters="kycFilter"
                       v-bind:columns="kycColumns" :has-selection="false"/>
          </b-tab>
          <b-tab :title="$t('tabs.clients.logs')" @click="()=>{ $refs.logTable.refresh(); $refs.callLogTable && $refs.callLogTable.refresh(); }">
            <template #title>
              <span class="cm-tab-pill">
                <i class="fas fa-circle-notch fa-spin" v-if="$refs.logTable && $refs.logTable.loading === true"></i>
                <i class="fa-sharp fa-solid fa-newspaper" v-else></i>
                {{ $t('tabs.clients.logs') }}
              </span>
            </template>
            <h6 class="mt-50 mb-50"><i class="fas fa-phone mr-25"></i>{{ $t('callLog.title') }}</h6>
            <call-log-table ref="callLogTable" v-bind:filters="logFilter"/>
            <h6 class="mt-1 mb-50"><i class="fas fa-sign-in-alt mr-25"></i>{{ $t('callLog.logins') }}</h6>
            <log-table ref="logTable" v-bind:filters="logFilter" v-bind:columns="logColumns" :has-selection="false"/>
          </b-tab>
          <b-tab :title="$t('tabs.clients.activity')" lazy>
            <template #title>
              <span class="cm-tab-pill">
                <i class="fa-sharp fa-solid fa-timeline"></i>
                {{ $t('tabs.clients.activity') }}
              </span>
            </template>
            <client-activity-timeline :client-id="client.id" scrollable/>
          </b-tab>
        </b-tabs>
        <div v-else>
          <client-new-details v-model="client"/>
        </div>

      </div>
      <div class="text-center position-absolute d-block w-100" style="top:50%"
           v-else-if="loading !== true && error != null"
      >
        <b-alert show variant="danger">
          <i class="fa-sharp fa-solid fa-triangle-exclamation"></i>
          {{ error }}
        </b-alert>
      </div>
      <div class="text-center position-absolute d-block w-100" style="top:50%" v-else>
        <b-spinner>{{ $t('operations.loading') }}</b-spinner>
      </div>
    </div>

    <!--modals-->
    <div v-if="client != null">
      <b-modal no-close-on-backdrop ref="copyClientModal" :title="'Copy '+client.fullName" hide-footer>
        <div>
          <b-form-group label="New Email" label-for="newEmail">
            <b-form-input v-model="newEmail" id="newEmail" required></b-form-input>
          </b-form-group>
          <div class="text-center mt-2">
            <b-button :disabled="newEmail==null || newEmail===''" class="d-inline-block mr-1" size="sm"
                      variant="outline-success" @click="confirmCopyClient"
            >Confirm
            </b-button>
            <b-button class="d-inline-block" size="sm" variant="outline-warning" @click="cancelCopyClient">Cancel
            </b-button>
          </div>
        </div>
      </b-modal>
      <b-modal no-close-on-backdrop ref="promoteClientModal" :title="client.fullName" hide-footer>
        <b-row>

          <b-col cols="12">
            <b-form-group label="Branch">
              <v-select v-model="selectedBranch" :clearable="true"
                        :options="allBranchesFiltered()"
                        label="name"
                        :reduce="(type) => type.id"
                        @option:selected="filterUserByBranch"

              ></v-select>
            </b-form-group>
          </b-col>
          <b-col v-if="selectedBranch!=null && users!=null" cols="12">
            <b-form-group label="Manager">
              <v-select v-model="selectedManager" :clearable="true"
                        :options="users"
                        label="username"
                        :reduce="(type) => type.id"

              ></v-select>
            </b-form-group>
          </b-col>


        </b-row>
        <b-row>
          <b-col cols="3" class="text-center">
          </b-col>
          <b-col cols="3" class="text-center">
            <b-button :disabled="selectedOptionIsEmpty()" class="" size="sm" variant="outline-success"
                      @click="confirmPromoteClient"
            >Confirm
            </b-button>
          </b-col>
          <b-col cols="2">
            <b-button size="sm" variant="outline-warning" @click="cancelPromoteClient">Cancel
            </b-button>
          </b-col>
          <b-col cols="3" class="text-center">
          </b-col>
        </b-row>
      </b-modal>
    </div>
  </b-modal>


</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import ClientDetails from '@/components/partial/clients/ClientEditDetails';
import mixinBase from '@/components/mixin/mixinBase';
import ClientAccounts from '@/components/partial/accounts/ClientAccounts';
import ClientMonetaryTable from '@/components/tables/ClientMonetariesTable';
import mixinPopover from '@/components/mixin/mixinPopover';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import ClientNewDetails from '@/components/partial/clients/ClientNewDetails.vue';
import KycTable from '@/components/tables/KycTable';
import LogTable from '@/components/tables/LogTable.vue';
import CallLogTable from '@/components/tables/CallLogTable.vue';
import ClientActivityTimeline from '@/components/partial/clients/ClientActivityTimeline.vue';

export default {
  name: 'clientModal',
  mixins: [mixinBase, mixinNotifications, mixinPopover, mixinPermissions],
  components: {
    LogTable,
    CallLogTable,
    ClientActivityTimeline,
    KycTable,
    ClientNewDetails,
    ClientMonetaryTable,
    ClientAccounts,
    ClientDetails
  },
  data: () => ({
    dialog: {
      show: false,
      tab: 0,
    },

    selectedBranch: null,
    selectedManager: null,
    newEmail: '',
    kycFilter: {},
    logFilter: {},
    kycColumns: [],
    logColumns: [],
    uniqueId: 0,
    loading: true,
    reLoading: true,
    loadingToken: false,
    error: null,
    id: null,
    client: null,
    branches: null,
    users: null,

  }),
  watch: {
    ['dialog.tab']() {
      let queryParams = this.queryParams();
      if (this.client != null && this.loading !== true && queryParams.tab !== this.dialog.tab) {
        this.pushState({ tab: this.dialog.tab });
      }
    },
    queryParams: {
      handler() {
        let queryParams = this.queryParams();
        if (queryParams.clientId == null) {
          this.closeModal();
          return;
        }
        if (queryParams.clientId !== this.id) {
          this.showModal(queryParams.clientId);
        } else if (queryParams.tab !== this.dialog.tab) {
          this.dialog.tab = !isNaN(queryParams.tab) ? parseInt(queryParams.tab) : 0;
        }

      },
      deep: true
    },
    ['client.id']() {
      if (this.client != null && this.client.id != null) {
        this.pushState({ clientId: this.client.id });
        this.id = this.client.id;

        this.kycFilter = {
          clientId: this.client.id
        };
        this.logFilter = {
          id: this.client.id
        };
      }
    }
  },
  mounted() {
    this.$root.$on('open-client-modal', this.handleExternalOpen);
    this.$root.$on('system::account::update', this.onAccountChange);
  },
  beforeDestroy() {
    this.$root.$off('open-client-modal', this.handleExternalOpen);
    this.$root.$off('system::account::update', this.onAccountChange);
  },

  computed: {
    ...mapGetters('data', ['allUsers', 'allBusinessUnits']),
    monetaryColumnsNames() {
      return [
        {
          'key': 'id',
          'locked': false,
          'enable': true
        },
        {
          'key': 'type',
          'locked': false,
          'enable': true
        },
        {
          'key': 'clientDetails.accountId',
          'label': 'account',
          'locked': false,
          'enable': true
        },
        {
          'key': 'amount',
          'locked': false,
          'enable': true
        },
        {
          'key': 'currency',
          'locked': false,
          'enable': true
        },
        {
          'key': 'registrationTime',
          'locked': false,
          'enable': true
        },
        {
          'key': 'wallet',
          'locked': false,
          'enable': true
        },
        {
          'key': 'realType',
          'locked': false,
          'enable': true
        }, {
          'key': 'action',
          'locked': false,
          'enable': true
        },
        {
          'key': 'depositorUserId',
          'locked': false,
          'enable': true
        },
        {
          'key': 'flags',
          'label': 'ftdStd',
          'locked': false,
          'enable': true
        }
      ];
    },
    clientCurrency(){
      if(Array.isArray(this.client?.accounts) && this.client.accounts.length > 0){
        return this.client.accounts[0].currency
      }
      return null;
    },
    avatarInitials(){
      if(this.client == null) return '';
      const f = (this.client.firstName || '').trim();
      const l = (this.client.lastName || '').trim();
      const a = f ? f[0] : '';
      const b = l ? l[0] : '';
      return (a + b).toUpperCase() || '?';
    },
    hasPhonePermission(){
      return this.hasPermission && this.hasPermission(this.permissions ? this.permissions.CLIENT_READ_PHONE : null);
    }
  },
  created() {
    this.uniqueId = this._uid;
    let queryParams = this.queryParams();
    if (queryParams.clientId != null) {
      this.showModal(queryParams.clientId);
    }
    this.setKycClientFields();
    this.setLogClientFields();
  },
  methods: {
    ...mapActions('clients', ['getClientById', 'duplicateClient', 'getClientToken', 'promoteClient', 'demoteClient']),

    saveClientDetails() {
      if (this.$refs.details && this.$refs.details.update) {
        this.$refs.details.update();
      } else {
        console.warn("client-details component not mounted or no update() method found.");
      }
    },
    quickCall() {
      if (!this.client || !this.client.phone) return;
      this.dialog.tab = 0;
      this.$nextTick(() => {
        const d = this.$refs.details;
        if (d && typeof d.initiateCallClick === 'function') d.initiateCallClick('phone');
      });
    },
    quickEmail() {
      this.dialog.tab = 0;
      this.$nextTick(() => {
        const d = this.$refs.details;
        if (d && typeof d.openEmailEditor === 'function') d.openEmailEditor();
      });
    },
    quickNote() {
      this.dialog.tab = 0;
      this.$nextTick(() => {
        const d = this.$refs.details;
        if (d && typeof d.openNoteModal === 'function') d.openNoteModal();
      });
    },
    quickSchedule() {
      this.dialog.tab = 0;
      this.$nextTick(() => {
        const d = this.$refs.details;
        if (d && typeof d.openEventModal === 'function') d.openEventModal();
      });
    },
    handleExternalOpen(id) {
      if (this.id !== id) {
        this.showModal(id);
      } else if (!this.dialog.show) {
        this.dialog.show = true;
      }
    },
    selectedOptionIsEmpty() {
      return this.selectedBranch === null || this.selectedManager === null;
    },
    openCopyClientModal() {
      this.newEmail = null;
      this.$refs.copyClientModal.show();
    },
    openPromoteClientModal() {
      this.selectedBranch = null;
      this.selectedManager = null;
      this.$refs.promoteClientModal.show();
    },
    confirmCopyClient() {
      if (this.newEmail === '') {
        return;
      }
      let $this = this;
      this.duplicateClient({
        id: this.id,
        newEmail: this.newEmail
      })
          .then(client => {
            $this.client = client;
            $this.error = null;
            $this.loading = false;
            $this.$refs.copyClientModal.hide();
          }, err => {
            $this.error = $this.getErrorDescription(err);
            $this.loading = false;
          });
    },
    confirmPromoteClient() {
      if (this.selectedBranch == null || this.selectedManager == null) {
        return;
      }
      let $this = this;
      // Moving a PROMOTED client (isSale === false) back to a SALES branch is a
      // demotion — it must stamp demotedTime so he leaves the FTD stats.
      // Everything else (lead moves, promotion to retention) stays a promote.
      const targetBranch = (this.allBusinessUnits || []).find(b => b.id === this.selectedBranch);
      const isDemotion = targetBranch && targetBranch.type === 'SALES' && this.client && this.client.isSale === false;
      const moveAction = isDemotion ? this.demoteClient : this.promoteClient;
      moveAction({
        id: this.id,
        userId: this.selectedManager,
        branchId: this.selectedBranch,
      })
          .then(client => {
            $this.client = client;
            $this.error = null;
            $this.loading = false;
            $this.selectedBranch = null;
            $this.selectedManager = null;
            $this.users = null;
            $this.$refs.promoteClientModal.hide();
          }, err => {
            $this.error = $this.getErrorDescription(err);
            $this.loading = false;
          });
    },
    cancelCopyClient() {
      this.newEmail = '';
      this.$refs.copyClientModal.hide();
    },
    cancelPromoteClient() {
      this.selectedBranch = null;
      this.users = null;
      this.$refs.promoteClientModal.hide();
    },
    showModal(id) {
      let queryParams = this.queryParams();
      console.log('showModal-> client', id);
      this.id = id;
      if (id) {
        this.loading = true;
        this.dialog.show = true;
        this.dialog.tab = !isNaN(queryParams.tab) ? parseInt(queryParams.tab) : 0;
        this.refreshData();
        this.loading = true;
        this.pushState({
          clientId: id,
          tab: this.dialog.tab,
        });
      } else {
        this.client = {};
        this.error = null;
        this.loading = false;
        this.dialog.show = true;
      }
    },
    closeModal() {
      this.dialog.show = false;
      this.client = {};
      this.onDialogClose();
    },
    onDialogClose() {
      this.id = null;
      this.pushState({
        clientId: null,
        tab: null,
      });
      this.$emit("close");
    },
    refreshData() {
      let $this = this;
      this.reLoading = true;
      let queryParams = this.queryParams();
      let currentTab = !isNaN(queryParams.tab) ? parseInt(queryParams.tab) : 0;
      this.getClientById(this.id)
          .then(client => {
            if(this.client == null){
              setTimeout(() => {
                $this.dialog.tab = currentTab;
              }, 500);
            }
            $this.client = client;
            $this.$emit("clientName", client.fullName)
            $this.error = null;
            $this.loading = false;
            $this.reLoading = false;

          }, err => {
            $this.error = $this.getErrorDescription(err);
            $this.loading = false;
            $this.reLoading = false;
          });
    },
    refreshDataAfterMonetary() {
      let $this = this;
      this.reLoading = true;
      this.getClientById(this.id)
          .then(client => {
            $this.client = client;
            $this.error = null;
            $this.loading = false;
            $this.reLoading = false;

          }, err => {
            $this.error = $this.getErrorDescription(err);
            $this.loading = false;
            $this.reLoading = false;
          });
    },
    onAccountChange(account){
      if(this.dialog.show !== true){
        return
      }
      if(this.client == null || this.client.id == null){
        return
      }
      if(account.clientId !== this.client.id){
        return
      }
      this.refreshDataAfterMonetary();
    },
    setKycClientFields() {

      this.kycColumns = [
        {
          'key': 'status',
          'locked': false,
          'enable': true
        },
        {
          'key': 'type',
          'locked': false,
          'enable': true
        },
        {
          'key': 'file.source',
          'locked': false,
          'enable': true
        },
        {
          'key': 'info',
          'locked': false,
          'enable': false
        },
        {
          'key': 'registrationTime',
          'locked': false,
          'enable': true
        }, {
          'key': 'operationTime',
          'locked': false,
          'enable': true
        }
      ];
    },

    setLogClientFields() {
      this.logColumns = [
        {
          key: 'clientId',
          locked: false,
          enable: true
        },

        {
          key: 'host',
          locked: false,
          enable: true
        },
        {
          key: 'ip',
          locked: false,
          enable: true
        },
        {
          key: 'loginTime',
          locked: false,
          enable: true
        },
        {
          key: 'failedReason',
          locked: false,
          enable: true
        }
      ];
    }
,
    allBranchesFiltered() {
      if (this.client.isSale == null || this.client.isSale) {
        return this.allBusinessUnits.filter(branch => {
          return branch.type === 'RETENTION';
        });
      } else {
        return this.allBusinessUnits.filter(branch => {
          return branch.type === 'SALES';
        });
      }
    },

    filterUserByBranch() {
      this.users = this.allUsers.filter(user => {
        return user.businessUnits && user.businessUnits.some(branch => branch === this.selectedBranch);
      });
    },
    async openMyAccount() {
      this.loadingToken = true;
      try {
        let token = await this.getClientToken(this.client.id);
        window.open(token.accountPath, '_blank');
      } catch (e) {
        this.showServerErrorNotification('connectionError', null, e);
      } finally {
        this.loadingToken = false;
      }
    }
  },
};
</script>

<style scoped>
/* ===== Tokens (light) ===== */
.cm-content {
  --cm-bg: #ffffff;
  --cm-bg-alt: #f8fafc;
  --cm-border: #e2e8f0;
  --cm-border-strong: #cbd5e1;
  --cm-text: #0f172a;
  --cm-text-2: #475569;
  --cm-text-muted: #94a3b8;
  --cm-primary: #2563eb;
  --cm-primary-strong: #1d4ed8;
  --cm-primary-soft: #eff6ff;
  --cm-success: #16a34a;
  --cm-success-soft: #f0fdf4;
  --cm-warn: #f59e0b;
  --cm-warn-soft: #fffbeb;
  --cm-danger: #dc2626;
  --cm-danger-soft: #fef2f2;

  padding: 0;
  color: var(--cm-text);
}

.dark-layout .cm-content {
  --cm-bg: #1e293b;
  --cm-bg-alt: #0f172a;
  --cm-border: #334155;
  --cm-border-strong: #475569;
  --cm-text: #f1f5f9;
  --cm-text-2: #cbd5e1;
  --cm-text-muted: #94a3b8;
  --cm-primary: #60a5fa;
  --cm-primary-strong: #3b82f6;
  --cm-primary-soft: rgba(96, 165, 250, 0.15);
  --cm-success-soft: rgba(34, 197, 94, 0.15);
  --cm-warn-soft: rgba(245, 158, 11, 0.15);
  --cm-danger-soft: rgba(239, 68, 68, 0.15);
}

.cm-test-badge {
  background: var(--cm-warn);
  color: #4a3500;
  font-style: normal;
  font-size: 0.65rem;
  font-weight: 800;
  padding: 2px 7px;
  border-radius: 4px;
  margin-right: 6px;
  letter-spacing: 0.7px;
}

/* ===== HERO ===== */
.cm-hero {
  position: relative;
  background: linear-gradient(135deg, #eff6ff 0%, #f0fdf4 50%, #fef3c7 100%);
  border-radius: 14px;
  padding: 11px 18px 11px;
  margin-bottom: 12px;
  border: 1px solid var(--cm-border);
  overflow: hidden;
}

.dark-layout .cm-hero {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.18) 0%, rgba(22, 163, 74, 0.12) 50%, rgba(245, 158, 11, 0.12) 100%);
  border-color: var(--cm-border);
}

.cm-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 0%, rgba(37, 99, 235, 0.08) 0%, transparent 60%);
  pointer-events: none;
}

.cm-hero-row {
  display: flex;
  align-items: center;
  gap: 13px;
  flex-wrap: wrap;
  position: relative;
}

.cm-avatar {
  position: relative;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 50%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 1.02rem;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4), inset 0 -2px 0 rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.cm-presence {
  position: absolute;
  bottom: -3px;
  right: -3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid var(--cm-bg);
  background: #cbd5e1;
}

.cm-avatar.online .cm-presence { background: var(--cm-success); box-shadow: 0 0 0 2px rgba(22, 163, 74, 0.25); }
.cm-avatar.offline .cm-presence { background: var(--cm-danger); }

.cm-hero-name-block { flex: 1; min-width: 0; }

.cm-hero-name {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.cm-hero-name-text {
  font-size: 1.08rem;
  font-weight: 800;
  color: var(--cm-text);
  letter-spacing: -0.2px;
}

.cm-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.62rem;
  font-weight: 800;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.cm-pill-test { background: var(--cm-warn); color: #4a3500; }
.cm-pill-warn { background: var(--cm-danger-soft); color: var(--cm-danger); border: 1px solid rgba(220, 38, 38, 0.25); }

.cm-hero-sub {
  display: flex;
  gap: 14px;
  margin-top: 2px;
  font-size: 0.76rem;
  color: var(--cm-text-2);
  flex-wrap: wrap;
}

.cm-sub-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
}

/* Stats */
.cm-stats { display: flex; gap: 10px; flex-wrap: wrap; }

.cm-stat {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid var(--cm-border);
  border-radius: 10px;
  padding: 5px 12px;
  min-width: 118px;
  display: flex;
  gap: 9px;
  align-items: center;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.dark-layout .cm-stat {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
}

.cm-stat-icon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
}

.cm-stat-balance .cm-stat-icon { background: var(--cm-warn-soft); color: var(--cm-warn); }
.cm-stat-credit .cm-stat-icon { background: var(--cm-primary-soft); color: var(--cm-primary); }

.cm-stat-label {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--cm-text-muted);
}

.cm-stat-value {
  font-size: 0.97rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--cm-text);
  line-height: 1.15;
}

.cm-stat-value-empty { color: var(--cm-text-muted); }
.cm-stat-positive { color: var(--cm-success); }
.cm-stat-negative { color: var(--cm-danger); }

/* ===== ACTION BAR ===== */
.cm-action-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 9px;
  padding-top: 9px;
  border-top: 1px solid rgba(255, 255, 255, 0.6);
  position: relative;
  flex-wrap: wrap;
}

.dark-layout .cm-action-bar { border-top-color: rgba(255, 255, 255, 0.08); }

.cm-quick-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--cm-border);
  color: var(--cm-text);
  font-size: 0.8rem;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.18s ease;
}

.dark-layout .cm-quick-action {
  background: rgba(15, 23, 42, 0.6);
}

.cm-quick-action:hover:not(:disabled) {
  background: var(--cm-primary-soft);
  color: var(--cm-primary);
  border-color: var(--cm-primary);
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(37, 99, 235, 0.18);
}

.cm-quick-action:disabled { opacity: 0.45; cursor: not-allowed; }

.cm-quick-action i { font-size: 0.85rem; }

.cm-quick-primary {
  background: var(--cm-primary);
  color: #fff;
  border-color: var(--cm-primary);
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.25);
}
.cm-quick-primary:hover:not(:disabled) {
  background: var(--cm-primary-strong);
  color: #fff;
  border-color: var(--cm-primary-strong);
}

.cm-action-spacer { flex: 1; min-width: 6px; }

.cm-actions-dropdown >>> .btn {
  border-radius: 9px;
  padding: 6px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.92);
  border-width: 1.5px;
}
.cm-actions-dropdown >>> .btn:hover {
  background: var(--cm-primary);
  color: #fff;
  border-color: var(--cm-primary);
}
.dark-layout .cm-actions-dropdown >>> .btn { background: rgba(15, 23, 42, 0.6); }
</style>

<style>
/* Global rules — modal sizing must be unscoped because b-modal renders outside our component subtree */
.cm-dialog .modal-content {
  min-height: calc(100vh - 60px);
}

.cm-body {
  min-height: calc(100vh - 60px);
  display: flex;
  flex-direction: column;
}

.cm-body > .cm-content,
.cm-body > div {
  flex: 1 1 auto;
}

/* Global (un-scoped) — dropdown menu lives outside the scoped subtree once boundary=viewport */
.cm-actions-menu {
  min-width: 220px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18), 0 2px 6px rgba(15, 23, 42, 0.08);
  padding: 6px;
  z-index: 2000;
}
.cm-actions-menu .dropdown-item {
  border-radius: 7px;
  padding: 7px 12px;
  font-size: 0.85rem;
  font-weight: 500;
  transition: background 0.15s ease, color 0.15s ease;
}
.cm-actions-menu .dropdown-item:hover {
  background: #eff6ff;
  color: #2563eb;
}
.cm-actions-menu .dropdown-item:active {
  background: #2563eb;
  color: #fff;
}
.dark-layout .cm-actions-menu {
  background: #2a2f3a;
  border-color: #475569;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}
.dark-layout .cm-actions-menu .dropdown-item {
  color: #e2e6ee;
}
.dark-layout .cm-actions-menu .dropdown-item:hover {
  background: rgba(96, 165, 250, 0.18);
  color: #6ab0ff;
}

.cm-save-btn {
  border-radius: 9px;
  padding: 6px 18px;
  font-weight: 700;
  font-size: 0.82rem;
  box-shadow: 0 3px 8px rgba(37, 99, 235, 0.3);
}

/* ===== TABS ===== */
.cm-tabs >>> .cm-tabs-nav {
  border: none;
  border-bottom: 1px solid var(--cm-border);
  padding: 0 4px;
  gap: 2px;
}

.cm-tabs >>> .cm-tabs-nav .nav-link {
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: 11px 18px 13px;
  font-size: 0.86rem;
  font-weight: 600;
  color: var(--cm-text-muted);
  transition: color 0.18s ease, border-color 0.18s ease, background 0.18s ease;
  background: transparent;
  margin-bottom: -1px;
}

.cm-tabs >>> .cm-tabs-nav .nav-link:hover {
  color: var(--cm-primary);
  background: var(--cm-primary-soft);
}

.cm-tabs >>> .cm-tabs-nav .nav-link.active {
  color: var(--cm-primary);
  background: transparent;
  border-bottom: 2px solid var(--cm-primary);
  font-weight: 700;
}

.cm-tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.cm-tab-pill i { font-size: 0.88rem; }

.cm-tabs >>> .cm-tabs-content {
  padding: 18px 6px 6px;
}
</style>
